import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CaseManagement = ({ onCaseSelect, onNewCase }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('deadline');

  // Mock case data
  const mockCases = [
    {
      id: 'case_001',
      title: 'Climate Change Data Verification',
      claim: 'Global temperatures have increased by 2.5°C since 1990 according to new EU climate report',
      source: 'Social Media Post',
      priority: 'high',
      status: 'in_progress',
      assignedTo: 'You',
      createdAt: new Date(Date.now() - 86400000),
      deadline: new Date(Date.now() + 172800000),
      progress: 65,
      methodology: 'European Code of Standards',
      tags: ['climate', 'statistics', 'eu-policy'],
      lastActivity: new Date(Date.now() - 3600000)
    },
    {
      id: 'case_002',
      title: 'Social Media Misinformation Analysis',
      claim: 'New EU immigration policy allows unlimited border crossings without documentation',
      source: 'Twitter/X',
      priority: 'critical',
      status: 'peer_review',
      assignedTo: 'Dr. Elena Rossi',
      createdAt: new Date(Date.now() - 172800000),
      deadline: new Date(Date.now() + 86400000),
      progress: 85,
      methodology: 'EFCSN Standards',
      tags: ['immigration', 'policy', 'social-media'],
      lastActivity: new Date(Date.now() - 1800000)
    },
    {
      id: 'case_003',
      title: 'Healthcare Misinformation Assessment',
      claim: 'EU approves new vaccine mandate for all member states effective immediately',
      source: 'News Website',
      priority: 'medium',
      status: 'completed',
      assignedTo: 'Marcus Weber',
      createdAt: new Date(Date.now() - 259200000),
      deadline: new Date(Date.now() - 86400000),
      progress: 100,
      methodology: 'Medical Fact-Check Protocol',
      tags: ['healthcare', 'vaccines', 'policy'],
      lastActivity: new Date(Date.now() - 86400000),
      verdict: 'false',
      confidence: 92
    },
    {
      id: 'case_004',
      title: 'Economic Statistics Fact-Check',
      claim: 'EU GDP growth reaches historic 8.5% in Q3 2024, surpassing all previous records',
      source: 'Blog Post',
      priority: 'medium',
      status: 'draft',
      assignedTo: 'You',
      createdAt: new Date(Date.now() - 43200000),
      deadline: new Date(Date.now() + 259200000),
      progress: 25,
      methodology: 'Statistical Verification Protocol',
      tags: ['economics', 'statistics', 'gdp'],
      lastActivity: new Date(Date.now() - 7200000)
    },
    {
      id: 'case_005',
      title: 'Political Statement Verification',
      claim: 'European Commission announces immediate dissolution of current parliament structure',
      source: 'News Article',
      priority: 'critical',
      status: 'escalated',
      assignedTo: 'Dr. Sarah Johnson',
      createdAt: new Date(Date.now() - 21600000),
      deadline: new Date(Date.now() + 43200000),
      progress: 45,
      methodology: 'Political Fact-Check Standards',
      tags: ['politics', 'eu-commission', 'governance'],
      lastActivity: new Date(Date.now() - 900000)
    }
  ];

  const filteredCases = mockCases?.filter(case_ => {
    const matchesSearch = case_?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         case_?.claim?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         case_?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    
    const matchesFilter = filterStatus === 'all' || case_?.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const sortedCases = [...filteredCases]?.sort((a, b) => {
    switch (sortBy) {
      case 'deadline':
        return new Date(a.deadline) - new Date(b.deadline);
      case 'priority':
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
      case 'progress':
        return b?.progress - a?.progress;
      case 'created':
        return new Date(b.createdAt) - new Date(a.createdAt);
      default:
        return 0;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'in_progress': return 'bg-primary/10 text-primary';
      case 'peer_review': return 'bg-warning/10 text-warning';
      case 'completed': return 'bg-success/10 text-success';
      case 'escalated': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-destructive';
      case 'high': return 'text-warning';
      case 'medium': return 'text-primary';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-success';
    if (progress >= 50) return 'bg-primary';
    if (progress >= 25) return 'bg-warning';
    return 'bg-muted';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="FolderOpen" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Case Management</h3>
            <p className="text-sm text-muted-foreground">
              {sortedCases?.length} cases • {sortedCases?.filter(c => c?.status === 'in_progress')?.length} active
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            onClick={onNewCase}
          >
            New Case
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Filter"
          >
            Filter
          </Button>
        </div>
      </div>
      {/* Search and Filters */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex space-x-2">
          <div className="flex-1">
            <Input
              placeholder="Search cases, claims, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Search"
          >
            Search
          </Button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-foreground">Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-2 py-1 border border-border rounded bg-input text-foreground text-sm"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="in_progress">In Progress</option>
              <option value="peer_review">Peer Review</option>
              <option value="completed">Completed</option>
              <option value="escalated">Escalated</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-foreground">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-2 py-1 border border-border rounded bg-input text-foreground text-sm"
            >
              <option value="deadline">Deadline</option>
              <option value="priority">Priority</option>
              <option value="progress">Progress</option>
              <option value="created">Created Date</option>
            </select>
          </div>
        </div>
      </div>
      {/* Cases List */}
      <div className="flex-1 overflow-y-auto">
        {sortedCases?.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h4 className="font-medium text-foreground mb-2">No cases found</h4>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search or filter criteria
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {sortedCases?.map((case_) => (
              <div
                key={case_?.id}
                className="p-4 bg-card border border-border rounded-lg hover:shadow-soft transition-smooth cursor-pointer"
                onClick={() => onCaseSelect?.(case_)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-foreground">{case_?.title}</h4>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(case_?.status)}`}>
                        {case_?.status?.replace('_', ' ')}
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {case_?.claim}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>Source: {case_?.source}</span>
                      <span>•</span>
                      <span>Assigned to: {case_?.assignedTo}</span>
                      <span>•</span>
                      <span>{case_?.methodology}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Icon
                      name="AlertCircle"
                      size={16}
                      className={getPriorityColor(case_?.priority)}
                    />
                    <span className={`text-xs font-medium ${getPriorityColor(case_?.priority)}`}>
                      {case_?.priority}
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Progress</span>
                    <span className="text-xs font-medium text-foreground">{case_?.progress}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${getProgressColor(case_?.progress)}`}
                      style={{ width: `${case_?.progress}%` }}
                    />
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {case_?.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-muted rounded text-xs text-muted-foreground"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>Created: {case_?.createdAt?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span className={case_?.deadline < new Date() ? 'text-destructive' : ''}>
                        Due: {case_?.deadline?.toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Icon name="Activity" size={12} />
                    <span>Last activity: {case_?.lastActivity?.toLocaleTimeString()}</span>
                  </div>
                </div>

                {/* Verdict (for completed cases) */}
                {case_?.verdict && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">Verdict:</span>
                      <div className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${case_?.verdict === 'true' ? 'bg-success/10 text-success' :
                          case_?.verdict === 'false'? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'}
                      `}>
                        {case_?.verdict}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Confidence: {case_?.confidence}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer Stats */}
      <div className="border-t border-border p-4 bg-muted/20">
        <div className="flex items-center justify-between text-sm">
          <div className="flex space-x-4">
            <span className="text-muted-foreground">
              Total: <span className="font-medium text-foreground">{mockCases?.length}</span>
            </span>
            <span className="text-muted-foreground">
              Active: <span className="font-medium text-primary">{mockCases?.filter(c => c?.status === 'in_progress')?.length}</span>
            </span>
            <span className="text-muted-foreground">
              Completed: <span className="font-medium text-success">{mockCases?.filter(c => c?.status === 'completed')?.length}</span>
            </span>
          </div>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
            >
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseManagement;